import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';
import { Shell } from './components/Shell';
import type { AuthState } from './lib/auth';
import type { DriverStatus, RideStatus, SosStatus, VehicleType } from './lib/types';
import { AccountPage } from './pages/Account';
import { AuditPage } from './pages/Audit';
import { PayoutDriverPage } from './pages/PayoutDriver';
import { PayoutsPage } from './pages/Payouts';
import { CommissionPage } from './pages/Commission';
import { RewardsPage } from './pages/Rewards';
import { RatingsPage } from './pages/Ratings';
import { DriverDetailPage } from './pages/DriverDetail';
import { DriversPage } from './pages/Drivers';
import { FaresPage } from './pages/Fares';
import { LivePage } from './pages/Live';
import { LoginPage } from './pages/Login';
import { RideDetailPage } from './pages/RideDetail';
import { RiderDetailPage } from './pages/RiderDetail';
import { RidersPage } from './pages/Riders';
import { RidesPage } from './pages/Rides';
import { SettingsPage } from './pages/Settings';
import { SosPage } from './pages/Sos';
import { StaffPage } from './pages/Staff';
import { NotFound } from './pages/NotFound';

export interface RouterContext {
  auth: AuthState;
  queryClient: QueryClient;
}

const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v : undefined);
const num = (v: unknown) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({ redirect: str(s.redirect) }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.admin) throw redirect({ to: search.redirect?.startsWith('/') ? search.redirect : '/' });
  },
  component: LoginPage,
});

/** Every staff page sits under this route: no session → the sign-in page, then back to where they were. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => {
    if (!context.auth.admin) throw redirect({ to: '/login', search: { redirect: location.href } });
  },
  component: Shell,
});

const liveRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: LivePage });

export const ridesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/rides',
  validateSearch: (s: Record<string, unknown>): { status?: RideStatus; type?: VehicleType; period?: 'today' | 'week'; page?: number } => ({
    status: str(s.status) as RideStatus | undefined,
    type: s.type === 'AUTO' || s.type === 'BIKE' || s.type === 'CAR' ? s.type : undefined,
    period: s.period === 'today' || s.period === 'week' ? s.period : undefined,
    page: num(s.page),
  }),
  component: RidesPage,
});
export const rideRoute = createRoute({ getParentRoute: () => appRoute, path: '/rides/$rideId', component: RideDetailPage });

export const driversRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/drivers',
  validateSearch: (s: Record<string, unknown>): { status?: DriverStatus; online?: boolean; type?: VehicleType; q?: string; page?: number } => ({
    status: str(s.status) as DriverStatus | undefined,
    online: s.online === true || s.online === 'true' ? true : undefined,
    type: s.type === 'AUTO' || s.type === 'BIKE' || s.type === 'CAR' ? s.type : undefined,
    q: str(s.q),
    page: num(s.page),
  }),
  component: DriversPage,
});
export const driverRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/drivers/$driverId',
  component: DriverDetailPage,
});

export const ridersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/riders',
  validateSearch: (s: Record<string, unknown>): { q?: string; blocked?: boolean; page?: number } => ({
    q: str(s.q),
    blocked: s.blocked === true || s.blocked === 'true' ? true : undefined,
    page: num(s.page),
  }),
  component: RidersPage,
});
export const riderRoute = createRoute({ getParentRoute: () => appRoute, path: '/riders/$riderId', component: RiderDetailPage });

const faresRoute = createRoute({ getParentRoute: () => appRoute, path: '/fares', component: FaresPage });
const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: '/settings', component: SettingsPage });
export const sosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sos',
  validateSearch: (s: Record<string, unknown>): { status?: SosStatus; page?: number } => ({
    status: str(s.status) as SosStatus | undefined,
    page: num(s.page),
  }),
  component: SosPage,
});
const staffRoute = createRoute({ getParentRoute: () => appRoute, path: '/staff', component: StaffPage });
export const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit',
  validateSearch: (s: Record<string, unknown>): { entityType?: string; page?: number } => ({
    entityType: str(s.entityType),
    page: num(s.page),
  }),
  component: AuditPage,
});
export const payoutsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/payouts',
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'payouts'; filter?: 'owes' | 'owed'; q?: string; status?: 'PAID' | 'CANCELLED' | 'ALL'; page?: number } => ({
    tab: s.tab === 'payouts' ? 'payouts' : undefined,
    filter: s.filter === 'owes' || s.filter === 'owed' ? s.filter : undefined,
    q: str(s.q),
    status: s.status === 'PAID' || s.status === 'CANCELLED' || s.status === 'ALL' ? s.status : undefined,
    page: num(s.page),
  }),
  component: PayoutsPage,
});
export const payoutDriverRoute = createRoute({ getParentRoute: () => appRoute, path: '/payouts/$driverId', component: PayoutDriverPage });
/** The old Cash page lives on as Payouts → Balances. */
const cashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cash',
  beforeLoad: ({ search }) => {
    const owing = (search as { owing?: string }).owing;
    throw redirect({ to: '/payouts', search: { filter: owing === 'driver' ? 'owes' : owing === 'zoomo' ? 'owed' : undefined } });
  },
});
export const ratingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ratings',
  validateSearch: (s: Record<string, unknown>): { show?: 'all'; by?: 'RIDER' | 'DRIVER'; page?: number } => ({
    show: s.show === 'all' ? 'all' : undefined,
    by: s.by === 'RIDER' || s.by === 'DRIVER' ? s.by : undefined,
    page: num(s.page),
  }),
  component: RatingsPage,
});
const commissionRoute = createRoute({ getParentRoute: () => appRoute, path: '/commission', component: CommissionPage });
export const rewardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/rewards',
  validateSearch: (s: Record<string, unknown>): { audience?: 'RIDER' | 'DRIVER'; page?: number } => ({
    audience: s.audience === 'RIDER' || s.audience === 'DRIVER' ? s.audience : undefined,
    page: num(s.page),
  }),
  component: RewardsPage,
});
const accountRoute = createRoute({ getParentRoute: () => appRoute, path: '/account', component: AccountPage });

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    liveRoute,
    ridesRoute,
    rideRoute,
    driversRoute,
    driverRoute,
    ridersRoute,
    riderRoute,
    cashRoute,
    payoutsRoute,
    payoutDriverRoute,
    ratingsRoute,
    faresRoute,
    commissionRoute,
    rewardsRoute,
    settingsRoute,
    sosRoute,
    staffRoute,
    auditRoute,
    accountRoute,
  ]),
]);

export function createAppRouter(context: RouterContext, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context,
    history,
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
